import React, { FC, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { REACT_APP_BUILD_ENV } from '../../constants/server.constants';

const confirmationUrl = (): string => {
  if (REACT_APP_BUILD_ENV === 'dev') {
    return 'http://localhost:3000/billing/confirmation';
  }
  if (REACT_APP_BUILD_ENV === 'staging') {
    return 'https://staging.coelab.co/billing/confirmation';
  }
  return 'https://app.coelab.co/billing/confirmation';
};

export interface ProgramCheckoutFormProps {
  /** Stay on this page after payment and report the PaymentIntent. Billing leaves this unset and redirects. */
  onPaid?: (paymentIntentId: string) => void;
  /** Save the card first. The plan is created only after this setup succeeds. */
  onSetup?: (paymentMethodId: string) => Promise<void> | void;
  buttonLabel?: string;
}

const ProgramCheckoutForm: FC<ProgramCheckoutFormProps> = ({ onPaid, onSetup, buttonLabel = 'Pay now' }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      setMessage('Stripe has not finished loading. Refresh the page and try again.');
      return;
    }
    setIsLoading(true);
    if (onSetup) {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: confirmationUrl(),
        },
      });
      if (error) {
        setMessage(error.message || 'Payment could not be completed');
        setIsLoading(false);
        return;
      }
      const paymentMethod = setupIntent?.payment_method;
      const paymentMethodId = typeof paymentMethod === 'string' ? paymentMethod : paymentMethod?.id;
      if (setupIntent?.status === 'succeeded' && paymentMethodId) {
        try {
          await onSetup(paymentMethodId);
        } catch {
          // The create-team dialog reports the plan error.
        }
      }
      setIsLoading(false);
      return;
    }
    if (onPaid) {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: confirmationUrl(),
        },
      });
      if (error) {
        setMessage(error.message || 'Payment could not be completed');
        setIsLoading(false);
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        try {
          await onPaid(paymentIntent.id);
        } catch {
          setMessage('Payment succeeded, but the team could not be created. Please try again.');
        }
      }
      setIsLoading(false);
      return;
    }
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: confirmationUrl(),
      },
    });
    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message || 'Payment could not be completed');
    } else {
      setMessage('An unexpected error occurred.');
    }
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="program-checkout-form">
      {message ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {message}
        </Alert>
      ) : null}
      <PaymentElement id="payment-element" options={{ layout: 'tabs' }} onReady={() => setPaymentReady(true)} />
      <Button
        type="submit"
        variant="contained"
        sx={{ mt: 2 }}
        disabled={isLoading || !stripe || !elements || !paymentReady}
      >
        {paymentReady ? buttonLabel : 'Loading payment form…'}
      </Button>
    </form>
  );
};

export default ProgramCheckoutForm;
